import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { SignedIn, SignedOut, useOrganization, useOrganizationList, useSession, SignOutButton } from "@clerk/clerk-react";
import { ArrowRight } from "@phosphor-icons/react";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Calendar from "@/pages/Calendar";
import MediaLibrary from "@/pages/MediaLibrary";
import Analytics from "@/pages/Analytics";
import ApprovalQueue from "@/pages/ApprovalQueue";
import Studio from "@/pages/Studio";
import Captions from "@/pages/Captions";
import Images from "@/pages/Images";
import AdCreator from "@/pages/AdCreator";
import AIVideo from "@/pages/AIVideo";
import CreativeDirector from "@/pages/CreativeDirector";
import BrandAssets from "@/pages/BrandAssets";
import Team from "@/pages/Team";
import Settings from "@/pages/Settings";
import Admin from "@/pages/Admin";
import Help from "@/pages/Help";
import Layout from "@/components/Layout";
import ErrorBoundary from "@/components/ErrorBoundary";
import StaffPortal from "@/pages/StaffPortal";
import { useRole } from "@/hooks/useRole";
import { usePlatformAdmin } from "@/hooks/usePlatformAdmin";
import { getTenant, orgMatchesTenant } from "@/config/tenants";

function AppRoutes() {
  const { isMember, canSeeAnalytics, canManageTeam, canManageSettings } = useRole();
  const { isPlatformAdmin } = usePlatformAdmin();

  // Client / approver: clean approval queue only.
  if (isMember) {
    return (
      <Routes>
        <Route path="/approvals" element={<ApprovalQueue />} />
        <Route path="*" element={<Navigate to="/approvals" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<Dashboard />} />
      <Route path="/calendar" element={<Calendar />} />
      <Route path="/studio" element={<Studio />} />
      <Route path="/captions" element={<Captions />} />
      <Route path="/images" element={<Images />} />
      <Route path="/ads" element={<AdCreator />} />
      <Route path="/video" element={<AIVideo />} />
      <Route path="/creative-director" element={<CreativeDirector />} />
      <Route path="/brand-assets" element={<BrandAssets />} />
      <Route path="/approvals" element={<ApprovalQueue />} />
      <Route path="/media" element={<MediaLibrary />} />
      <Route path="/help" element={<Help />} />
      {canSeeAnalytics && <Route path="/analytics" element={<Analytics />} />}
      {canManageTeam && <Route path="/team" element={<Team />} />}
      {canManageSettings && <Route path="/settings" element={<Settings />} />}
      {isPlatformAdmin && <Route path="/admin" element={<Admin />} />}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function OrgOnboarding() {
  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4" data-testid="org-onboarding">
      <div className="text-center max-w-md">
        <h1 className="text-3xl font-bold text-brand-near-black mb-2" style={{ fontFamily: "Montserrat", fontWeight: 700 }}>
          Access pending
        </h1>
        <p className="text-brand-muted-grey text-sm mb-8">
          Your account isn't a member of any workspace yet. Ask your admin to invite you — new workspaces
          aren't self-service, so this isn't something you can create yourself.
        </p>
        <SignOutButton>
          <button data-testid="org-onboarding-signout" className="bg-brand-teal text-white px-6 py-2.5 text-sm font-bold uppercase tracking-widest rounded-[10px]">
            Sign out
          </button>
        </SignOutButton>
      </div>
    </div>
  );
}

function FullLoading({ label = "Loading workspace…" }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white" data-testid="app-loading">
      <div className="text-brand-slate text-lg">{label}</div>
    </div>
  );
}

// Shown whenever the signed-in account belongs to MORE THAN ONE workspace — never trust Clerk's
// automatic "last active organization" default (it can silently pick the wrong one, e.g. right
// after a password reset starts a brand-new session). The user must explicitly pick every time.
function OrgPicker({ memberships, onChoose, busy }) {
  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4" data-testid="org-picker">
      <div className="w-full max-w-md">
        <h1 className="text-3xl font-bold text-brand-near-black mb-2 text-center" style={{ fontFamily: "Montserrat", fontWeight: 700 }}>
          Choose a workspace
        </h1>
        <p className="text-brand-muted-grey text-sm mb-8 text-center">
          Your account belongs to more than one workspace. Pick which one to open — you'll always
          be asked this on a fresh sign-in, so you never land in the wrong one by accident.
        </p>
        <div className="space-y-3">
          {memberships.map((m) => (
            <button
              key={m.organization.id}
              data-testid={`org-picker-option-${m.organization.id}`}
              disabled={busy}
              onClick={() => onChoose(m.organization.id)}
              className="w-full flex items-center justify-between px-5 py-4 border border-brand-border-grey hover:border-brand-teal hover:bg-brand-pale-teal transition-colors rounded-[10px] text-left disabled:opacity-50"
            >
              <span>
                <span className="block text-brand-near-black font-bold">{m.organization.name}</span>
                <span className="block text-brand-muted-grey text-xs uppercase tracking-wider mt-0.5">
                  {(m.role || "").replace("org:", "") === "admin" ? "Admin — full access" : (m.role || "").replace("org:", "") === "editor" ? "Editor" : "Member — approvals only"}
                </span>
              </span>
              <ArrowRight size={18} className="text-brand-teal shrink-0" />
            </button>
          ))}
        </div>
        <SignOutButton>
          <button data-testid="org-picker-signout" className="w-full mt-6 text-brand-muted-grey text-xs font-semibold uppercase tracking-widest hover:text-brand-near-black">
            Sign out
          </button>
        </SignOutButton>
      </div>
    </div>
  );
}

function PortalNoAccess({ tenant }) {
  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6 text-center" data-testid="portal-no-access">
      <p className="text-brand-teal text-xs font-bold uppercase tracking-[0.2em] mb-3">{tenant.shortName}</p>
      <h1 className="text-3xl font-bold text-brand-near-black mb-3" style={{ fontFamily: "Montserrat", fontWeight: 700 }}>
        You don't have access to this portal
      </h1>
      <p className="text-brand-slate text-sm max-w-md mb-8">
        Your account isn't a member of the {tenant.shortName} team. If you believe this is a mistake, please contact your administrator.
      </p>
      <SignOutButton>
        <button data-testid="portal-signout" className="bg-brand-teal text-white px-6 py-2.5 text-sm font-bold uppercase tracking-widest" style={{ borderRadius: 0 }}>
          Sign out
        </button>
      </SignOutButton>
    </div>
  );
}

function ActiveWorkspace() {
  return (
    <BrowserRouter basename="/app">
      <ErrorBoundary>
        <MemberOrFull />
      </ErrorBoundary>
    </BrowserRouter>
  );
}

// Branded (white-label) domain: auto-scope to the tenant org, no picker.
function TenantAuthedApp({ tenant }) {
  const { organization, isLoaded: orgLoaded } = useOrganization();
  const { isLoaded: listLoaded, userMemberships, setActive } = useOrganizationList({ userMemberships: true });

  const memberships = userMemberships?.data || [];
  const target = memberships.find((m) => orgMatchesTenant(tenant, m.organization?.name));

  useEffect(() => {
    if (!listLoaded || !orgLoaded || !target || !setActive) return;
    if (organization?.id !== target.organization.id) {
      setActive({ organization: target.organization.id });
    }
  }, [listLoaded, orgLoaded, target, organization?.id, setActive]);

  if (!listLoaded || !orgLoaded) return <FullLoading label={`Loading ${tenant.shortName}…`} />;
  if (!target) return <PortalNoAccess tenant={tenant} />;
  if (organization?.id !== target.organization.id) return <FullLoading label={`Loading ${tenant.shortName}…`} />;

  return <ActiveWorkspace />;
}

// Default domain: unchanged multi-workspace behaviour, but NEVER trust Clerk's automatic
// active-org pick when the account has more than one membership — force an explicit choice.
function DefaultAuthedApp() {
  const { organization, isLoaded: orgLoaded } = useOrganization();
  const { session } = useSession();
  const { isLoaded: listLoaded, userMemberships, setActive } = useOrganizationList({ userMemberships: { infinite: true } });
  const [chosenForSession, setChosenForSession] = useState(null);
  const [switching, setSwitching] = useState(false);

  // A brand-new session (fresh sign-in, password reset, etc.) must go through the picker again.
  useEffect(() => { setChosenForSession(null); }, [session?.id]);

  if (!orgLoaded || !listLoaded) return <FullLoading />;
  const memberships = userMemberships?.data || [];
  if (memberships.length === 0) return <OrgOnboarding />;

  if (memberships.length > 1 && !chosenForSession) {
    return (
      <OrgPicker
        memberships={memberships}
        busy={switching}
        onChoose={async (orgId) => {
          setSwitching(true);
          try {
            await setActive({ organization: orgId });
            setChosenForSession(orgId);
          } finally {
            setSwitching(false);
          }
        }}
      />
    );
  }
  if (!organization) return <FullLoading />;
  return <ActiveWorkspace />;
}

function AuthedApp() {
  const tenant = getTenant();
  if (tenant.whiteLabel) return <TenantAuthedApp tenant={tenant} />;
  return <DefaultAuthedApp />;
}

function MemberOrFull() {
  const { isMember, isLoaded } = useRole();
  if (!isLoaded) return <FullLoading />;
  if (isMember) {
    return <StaffPortal />;
  }
  return (
    <Layout>
      <ErrorBoundary>
        <AppRoutes />
      </ErrorBoundary>
    </Layout>
  );
}

function App() {
  useEffect(() => {
    document.title = getTenant().title;
  }, []);
  return (
    <>
      <SignedOut>
        <Login />
      </SignedOut>
      <SignedIn>
        <AuthedApp />
      </SignedIn>
    </>
  );
}

export default App;
